import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { UIProvider, useUI } from './context/UIContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ScrollToTop from './components/ScrollToTop';

const MainPage = lazy(() => import('./MainPage'));
const InteliMarkPortfolio = lazy(() => import('./InteliMarkPortfolio'));
const AgentsPage = lazy(() => import('./AgentsPage'));
const MarketingPage = lazy(() => import('./MarketingPage'));
const RebeccaAssistant = lazy(() => import('./RebeccaAssistant'));
const PortalSection = lazy(() => import('./PortalSection'));
const WebArchitecture = lazy(() => import('./WebArchitecture'));
const WebProjectInit = lazy(() => import('./WebProjectInit'));

const LoadingFallback = () => (
  <div className="fixed inset-0 bg-black flex items-center justify-center">
    <div className="text-primary font-mono text-sm tracking-[0.5em] animate-pulse">
      CARGANDO_SISTEMA...
    </div>
  </div>
);

const RebeccaWrapper = () => {
  const { isRebeccaOpen, closeRebecca } = useUI();
  return (
    <Suspense fallback={null}>
      <RebeccaAssistant isOpen={isRebeccaOpen} onClose={closeRebecca} />
    </Suspense>
  );
};

const GlobalStylesUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    // 1. Títulos de Rutas
    const routeTitles = {
      '/': 'Inicio',
      '/portfolio': 'Portfolio',
      '/agents': 'Agentes',
      '/marketing': 'Marketing',
      '/portal': 'Portal',
      '/web-design': 'Diseño Web',
      '/init-project': 'Iniciar Proyecto'
    };

    const hashTitles = {
      '#inicio': 'Inicio',
      '#nueva-vision': 'Visión',
      '#servicios': 'Servicios',
      '#agente-custom': 'Agente Custom',
      '#marketing-digital': 'Marketing Digital',
      '#diseno-web': 'Diseño Web',
      '#servicio-portafolio': 'Portafolio',
      '#medios': 'Prensa',
      '#directorio': 'Directorio',
      '#faq': 'FAQ'
    };

    const updateTitle = () => {
      const hash = window.location.hash;
      let sectionTitle = '';

      if (location.pathname === '/' && hash && hashTitles[hash]) {
        sectionTitle = hashTitles[hash];
      } else {
        sectionTitle = routeTitles[location.pathname] || 'Neural Engineering';
      }

      document.title = `InteliMark | ${sectionTitle}`;
    };

    updateTitle();
    window.addEventListener('hashchange', updateTitle);

    // 2. Gestión Global del Cursor
    // Solo en la Home ('/') ocultamos el cursor para mostrar el HUD de AutoCAD
    if (location.pathname === '/') {
        document.body.style.cursor = 'none';
    } else {
        document.body.style.cursor = 'auto';
    }

    return () => {
        window.removeEventListener('hashchange', updateTitle);
    };
  }, [location]);

  return null;
};

export default function App() {
  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
      <BrowserRouter>
        <ScrollToTop />
        <GlobalStylesUpdater />
        <UIProvider>
          <RebeccaWrapper />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<MainPage />} />
              <Route path="/portfolio" element={<InteliMarkPortfolio />} />
              <Route path="/agents" element={<AgentsPage />} />
              <Route path="/marketing" element={<MarketingPage />} />
              <Route path="/portal" element={<PortalSection />} />
              <Route path="/web-design" element={<WebArchitecture />} />
              <Route path="/init-project" element={<WebProjectInit />} />
            </Routes>
          </Suspense>
        </UIProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}
